// Varsler-feeden: kunngjøringer, situasjonsoppdateringer og reels — i hubben.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// EIERBESLUTNING B-001 (2026-08-18): «Hubben må gjerne se innholdet.»
// EIERBESLUTNING B-002 (2026-08-18): «jeg vil at vi skal se reels.»
// Se `vale/BESLUTNINGER.md` for hele begrunnelsen og hva som fortsatt er åpent.
//
// Feeden lå som ende-til-ende kryptert Matrix-rom, og virket ikke: hvert innlegg
// viste «Kunne ikke dekryptere meldingen». Bugen er rettet (bølge 206), men den
// var symptomet. Årsaken er at **per-enhet-megolm er feil verktøy for en
// kringkastingsfeed**: megolm deler en sesjon med de enhetene som var i rommet da
// meldingen ble SENDT, mens en feeds publikum er «alle i enheten, inkludert
// enheter som ikke finnes ennå». Under E2EE er dessuten server-side
// plakatbilder og kvalitetsvarianter umulige — og det er nettopp de som gjør
// forskjellen mellom en feed som ruller og en nedlastingskø.
//
// ── FIRE TING DENNE MODULEN NEKTER Å SKJULE ─────────────────────────────────
//  1. DATAKOSTNADEN. Ett minutt reel på 2 Mbps er ~15 MB. På feltnett er det en
//     reell kostnad, og `velgVariant` skal ALDRI stille returnere noe som
//     sprenger budsjettet — den returnerer `null` og lar flata si det.
//  2. KVITTERING ER IKKE EN REAKSJON. En «like» er sosial; en kvittering på en
//     ordre er ansvar. Den lagres med bruker-id og tid, og kan ikke trekkes.
//  3. LEVETID SKAL VÆRE OPPGITT. En «story» varer ikke 24 timer fordi det er
//     moten — den varer så lenge avsenderen sier, og `gjelderTilMs` er oppgitt.
//  4. PLAKATBILDE ER OBLIGATORISK FOR VIDEO. En video uten plakatbilde er et
//     svart rektangel, og et svart rektangel leses som «ødelagt».
import { z } from 'zod';
import { TrackOrgSchema } from './track.js';
// ── Media ────────────────────────────────────────────────────────────────────
export const FeedMediaTypeSchema = z.enum(['bilde', 'video']);
/**
 * Kvalitetstrinn. Tre er nok, og flere ville bare gjort valget uklart:
 * `lav` skal kunne spilles på et dårlig samband, `full` er originalen.
 */
export const MediekvalitetSchema = z.enum(['lav', 'middels', 'full']);
export const MEDIEKVALITET_TEKST = {
    lav: 'Lav (feltnett)',
    middels: 'Middels',
    full: 'Full kvalitet',
};
/**
 * Én transkodet variant av samme media.
 *
 * `bytes` er FAKTISK filstørrelse, ikke et anslag fra bitrate × varighet. Et
 * anslag som brukes til å velge variant på et smalt samband er et anslag som
 * bommer på nettopp det som var kritisk.
 */
export const MedievariantSchema = z.object({
    kvalitet: MediekvalitetSchema,
    bredde: z.number().int().positive().max(20_000),
    hoyde: z.number().int().positive().max(20_000),
    bytes: z.number().int().nonnegative(),
    /** Målt gjennomsnittlig bitrate (kbit/s). 0 = ukjent. */
    bitrateKbps: z.number().nonnegative().max(1_000_000).default(0),
    /** Sti i hubbens medialager. */
    sti: z.string().min(1).max(500),
});
/**
 * Ett media i et innlegg.
 *
 * ⚠ `plakatSti` ER PÅKREVD FOR VIDEO, og det er ikke pynt. Plakatbildet er det
 * som vises MENS videoen lastes, og det er hele grunnen til at en reels-flate
 * kjennes umiddelbar. Uten det ruller operatøren gjennom svarte rektangler og
 * konkluderer at det er ødelagt. Kravet håndheves i `FeedMediaSchema`.
 */
export const FeedMediaSchema = z
    .object({
    id: z.string().min(1).max(200),
    type: FeedMediaTypeSchema,
    /** Plakatbilde/miniatyr (JPEG). Påkrevd for video — se over. */
    plakatSti: z.string().max(500).optional(),
    varianter: z.array(MedievariantSchema).min(1).max(6),
    /** Varighet for video (ms). Udefinert for bilde. */
    varighetMs: z.number().int().nonnegative().optional(),
    /** Bildetekst / filnavn. */
    tekst: z.string().max(2000).optional(),
})
    .refine((m) => m.type !== 'video' || !!m.plakatSti, {
    message: 'video krever plakatSti (uten plakatbilde blir reels svarte rektangler)',
    path: ['plakatSti'],
})
    .refine((m) => m.type !== 'video' || (m.varighetMs ?? 0) > 0, {
    message: 'video krever varighetMs > 0 (varigheten styrer datakostnaden operatøren får se)',
    path: ['varighetMs'],
});
/**
 * Maks lengde på en reel.
 *
 * ⚠ ET TAK, IKKE EN SMAKSSAK. En ubegrenset opplasting over feltnett er et
 * tjenestenekt-angrep på eget samband: én lang video kan spise båndbredden som
 * skulle båret posisjoner og stridsmeldinger. 90 sekunder er nok til å vise en
 * akse, en stilling eller et objekt.
 */
export const REEL_MAKS_VARIGHET_MS = 90_000;
// ── Innlegget ────────────────────────────────────────────────────────────────
export const FeedInnleggTypeSchema = z.enum([
    /** Kunngjøring fra en sjef/stab. Kan kreve kvittering. */
    'kunngjoring',
    /** Kortlevd situasjonsoppdatering («story»), med oppgitt levetid. */
    'situasjon',
    /** Ekstern kilde (politiloggen, DATEX, OSINT). ⚠ Kan ALDRI kreve kvittering. */
    'ekstern',
]);
export const FeedInnleggSchema = z.object({
    id: z.string().min(1).max(200),
    type: FeedInnleggTypeSchema,
    /** Avsender. ⚠ Stemples server-side fra autentisert sesjon (§4 anti-spoof). */
    avsender: z.string().min(1).max(200),
    /** Kallesignal/rolle slik det skal LESES, ikke en rå Matrix-id. */
    avsenderRolle: z.string().max(200).optional(),
    tekst: z.string().max(20_000),
    tsMs: z.number().int(),
    /** Sist endret. 0 = aldri endret. */
    endretMs: z.number().int().nonnegative().default(0),
    /**
     * Når innlegget slutter å gjelde. Udefinert = står til det slettes.
     * ⚠ Oppgitt av avsenderen, ikke 24 timer fordi det er moten.
     */
    gjelderTilMs: z.number().int().optional(),
    media: z.array(FeedMediaSchema).max(10).default([]),
    /** Hastegrad — styrer om pushen bryter gjennom stillemodus. */
    presedens: z.enum(['flash', 'immediate', 'priority', 'routine']).default('routine'),
    /**
     * Krever kvittering fra mottakerne.
     * ⚠ Kan ikke settes på `ekstern` — se `FeedInnleggSchema`-refine under.
     */
    krevKvittering: z.boolean().default(false),
    /** Merker som peker inn i arkivet (`NAI-7`, `objekt-12`). Uten `#`. */
    merker: z.array(z.string().min(1).max(80)).max(20).default([]),
    lat: z.number().min(-90).max(90).optional(),
    lon: z.number().min(-180).max(180).optional(),
    org: TrackOrgSchema.optional(),
    /**
     * Opphav for eksterne innlegg: «Politiloggen (politiet.no, NLOD 2.0)».
     * ⚠ PÅKREVD når type er `ekstern`. En ekstern kilde som ikke oppgir opphav er
     * ikke til å skille fra en ordre — se refine.
     */
    kilde: z.string().max(300).optional(),
}).refine((i) => i.type !== 'ekstern' || !i.krevKvittering, {
    message: 'et eksternt innlegg kan ikke kreve kvittering — det er ingen ordre',
    path: ['krevKvittering'],
}).refine((i) => i.type !== 'ekstern' || !!i.kilde, {
    message: 'eksternt innlegg krever `kilde` (opphav skal ikke kunne forveksles med en ordre)',
    path: ['kilde'],
});
/** Én kvittering. Udelelig: bruker + tid. */
export const FeedKvitteringSchema = z.object({
    innleggId: z.string().min(1).max(200),
    bruker: z.string().min(1).max(200),
    tsMs: z.number().int(),
});
// ── Variantvalg: der datakostnaden blir ærlig ────────────────────────────────
/** Rekkefølge fra smalest til bredest. */
export const KVALITET_REKKEFOLGE = ['lav', 'middels', 'full'];
/**
 * Velg den beste varianten som PASSER innenfor `maksBytes`.
 *
 * ⚠ RETURNERER `null` NÅR INGEN PASSER, OG DET ER HELE POENGET.
 * Den nærliggende feilen er å falle tilbake på den minste varianten «så noe
 * spilles». Men er selv `lav` for stor for sambandet, er svaret ikke å laste den
 * likevel — det er å SI det, og la operatøren velge å bruke båndbredden. En
 * automatisk nedlasting som spiser sambandet under et oppdrag er verre enn en
 * video som ikke spiller.
 *
 * `maksBytes <= 0` betyr «ingen grense» (kablet/WiFi), og gir beste variant.
 */
export function velgVariant(varianter, maksBytes) {
    if (!varianter.length)
        return null;
    const sortert = [...varianter].sort((a, b) => KVALITET_REKKEFOLGE.indexOf(a.kvalitet) - KVALITET_REKKEFOLGE.indexOf(b.kvalitet));
    if (maksBytes <= 0)
        return sortert[sortert.length - 1];
    let valgt = null;
    for (const v of sortert) {
        if (v.bytes <= maksBytes)
            valgt = v;
        else
            break;
    }
    return valgt;
}
/** Menneskelesbar datakostnad. Alltid med «≈» — det er en filstørrelse, ikke et løfte om forbruk. */
export function dataKostnadTekst(bytes) {
    if (!Number.isFinite(bytes) || bytes <= 0)
        return 'ukjent størrelse';
    if (bytes < 1024)
        return `≈${Math.round(bytes)} B`;
    if (bytes < 1024 * 1024)
        return `≈${Math.round(bytes / 1024)} kB`;
    const mb = bytes / (1024 * 1024);
    return mb < 10 ? `≈${mb.toFixed(1)} MB` : `≈${Math.round(mb)} MB`;
}
/**
 * Skal denne videoen spilles av automatisk, og med hvilken variant?
 *
 * ⚠ PÅ MÅLT SAMBAND ER SVARET «BARE HVIS DEN ER LITEN NOK», ALDRI «ALLTID».
 * Autospill av alt i en rullende feed er hvordan man tømmer en datakvote og et
 * batteri i felt uten å ha bestemt seg for det.
 */
export function kanAutospille(media, policy) {
    if (media.type !== 'video') {
        return { spill: false, variant: null, grunn: 'Ikke en video.' };
    }
    if (!policy.maalt) {
        const beste = velgVariant(media.varianter, 0);
        return beste
            ? { spill: true, variant: beste, grunn: 'Fritt samband — full kvalitet.' }
            : { spill: false, variant: null, grunn: 'Ingen variant tilgjengelig.' };
    }
    if (policy.maksAutoBytes <= 0) {
        return {
            spill: false,
            variant: null,
            grunn: 'Autospill er av på målt samband. Trykk for å spille.',
        };
    }
    const v = velgVariant(media.varianter, policy.maksAutoBytes);
    if (!v) {
        const minste = velgVariant(media.varianter, Number.MAX_SAFE_INTEGER);
        const minsteBytes = Math.min(...media.varianter.map((x) => x.bytes));
        void minste;
        return {
            spill: false,
            variant: null,
            grunn: `For stor for autospill på målt samband (minste variant ${dataKostnadTekst(minsteBytes)}). `
                + 'Trykk for å laste den likevel.',
        };
    }
    return { spill: true, variant: v, grunn: `Målt samband — ${MEDIEKVALITET_TEKST[v.kvalitet]}.` };
}
// ── Kvittering ───────────────────────────────────────────────────────────────
/**
 * Kvitteringsstatus som tekst.
 *
 * ⚠ «X AV Y», ALDRI BARE «X». Et tall uten nevner svarer ikke på spørsmålet
 * sjefen faktisk har: *mangler det noen?* «14 kvitteringer» ser bra ut i en
 * tropp på 14 og alarmerende ut i en tropp på 40, og forskjellen er hele
 * grunnen til at feltet finnes.
 *
 * `forventet <= 0` betyr at mottakerlista ikke er kjent — og da sier vi det,
 * i stedet for å dele på noe vi ikke har.
 */
export function kvitteringSammendrag(antall, forventet) {
    const n = Math.max(0, Math.round(antall));
    if (forventet <= 0) {
        return `${n} kvittering${n === 1 ? '' : 'er'} · mottakerliste ukjent, så «mangler noen?» kan ikke besvares`;
    }
    const f = Math.round(forventet);
    const mangler = Math.max(0, f - n);
    if (mangler === 0)
        return `${n} av ${f} har kvittert · alle`;
    return `${n} av ${f} har kvittert · ${mangler} mangler`;
}
/** Har denne brukeren kvittert? */
export function harKvittert(kvitteringer, innleggId, bruker) {
    return kvitteringer.some((k) => k.innleggId === innleggId && k.bruker === bruker);
}
// ── Hashtags (FEED-11) ───────────────────────────────────────────────────────
/**
 * Fanger hashtags i feed-tekst — `#NAI-7`, `#RFI-19`, `#objekt-12`.
 *
 * ⚠ ENKEL REGEX MED VILJE (§8 i grunnloven), IKKE NLP. Alfanumerisk pluss
 * understrek/bindestrek er nok til å dekke id-formatene som faktisk finnes i
 * kontraktene (`Teig.id`, `Requirement.naiRef`, RFI/CT-id-er) — de er alle
 * frie strenger, ikke et fast skjema, så en «smart» parser ville gjettet på et
 * mønster som ikke er der. Tegnsetting (`.`, `)`, mellomrom …) stopper
 * matchen naturlig fordi de ikke er i tegnklassen.
 *
 * Case BEVARES som skrevet — det er OPPSLAGET (`losFeedTagg`) som er
 * ufølsomt for store/små bokstaver, ikke selve uttrekket. Dupliserte tagger i
 * samme tekst (uansett case) tas kun med én gang.
 */
const HASHTAG_RE = /#([A-Za-z0-9][A-Za-z0-9_-]{0,79})/g;
export function trekkUtHashtags(tekst) {
    const funnet = new Set();
    for (const m of tekst.matchAll(HASHTAG_RE))
        funnet.add(m[1]);
    return [...funnet];
}
/**
 * Slå sammen eksplisitt oppgitte merker (fra klienten) med hashtags funnet i
 * teksten, uten duplikater — og kappet til skjemaets `merker`-tak (20).
 *
 * ⚠ HASHTAGS ER EN TILLEGGSKILDE, IKKE EN OVERSTYRING. En avsender kan ha satt
 * `merker: ['NAI-7']` uten `#` i selve teksten (eldre klient, eller et merke
 * som ikke er ment å vises som lenke) — det skal ikke forsvinne fordi teksten
 * også inneholder hashtags.
 */
export function slaSammenMerker(eksplisitte, tekst) {
    const alle = new Set(eksplisitte);
    for (const h of trekkUtHashtags(tekst))
        alle.add(h);
    return [...alle].slice(0, 20);
}
// ── Levetid ──────────────────────────────────────────────────────────────────
/**
 * Er innlegget fortsatt gyldig?
 *
 * ⚠ UTGÅTT ER IKKE SLETTET. Et utgått innlegg skal fortsatt kunne leses (og ligge
 * i arkivet) — det skal bare ikke stå øverst som om det gjelder nå. Skjuler man
 * det helt, mister analytikeren det som var situasjonsbildet i går.
 */
export function erGyldig(i, naa) {
    return i.gjelderTilMs === undefined || i.gjelderTilMs > naa;
}
/** Gjenstående levetid som tekst, eller «utgått». */
export function levetidTekst(i, naa) {
    if (i.gjelderTilMs === undefined)
        return 'Ingen utløpstid oppgitt';
    const igjen = i.gjelderTilMs - naa;
    if (igjen <= 0)
        return 'Utgått';
    const min = Math.round(igjen / 60_000);
    if (min < 60)
        return `Gjelder ${min} min til`;
    const t = Math.round(igjen / 3_600_000);
    return t < 48 ? `Gjelder ${t} t til` : `Gjelder ${Math.round(igjen / 86_400_000)} døgn til`;
}
// ── Broen til observasjonsarkivet ────────────────────────────────────────────
/**
 * Feed-innlegg → arkivert observasjon.
 *
 * ⚠ ET INNLEGG MED BILDE OG STED **ER** EN OBSERVASJON, og skal ikke være en
 * kopi ved siden av arkivet. Samme post, samme id-rom, så analytikerens
 * arkivsøk i morgen finner det soldaten postet i dag. Dette er den samme broen
 * `sporTilObservasjon` er for spor.
 *
 * Returnerer `null` for innlegg som ikke hører i arkivet: uten sted er det ikke
 * en observasjon, og et `ekstern`-innlegg arkiveres av sin egen kilde-adapter
 * (med opphav og tilbaketrekking håndtert der), ikke som om vi hadde sett det.
 */
export function feedInnleggTilObservasjon(i, mottattMs) {
    if (i.lat === undefined || i.lon === undefined)
        return null;
    if (i.type === 'ekstern')
        return null;
    const forsteBilde = i.media.find((m) => m.type === 'bilde');
    const forsteVideo = i.media.find((m) => m.type === 'video');
    const o = {
        id: `feed:${i.id}`,
        tsMs: i.tsMs,
        mottattMs,
        kind: i.media.length ? 'bilde' : 'geotag',
        kilde: 'gna-feed',
        lat: i.lat,
        lon: i.lon,
        label: i.tekst.slice(0, 120) || 'Feed-innlegg',
    };
    if (i.org)
        o.org = i.org;
    o.rapportor = i.avsender;
    const bildeSti = forsteBilde?.varianter[0]?.sti ?? forsteVideo?.plakatSti;
    if (bildeSti)
        o.bildeUrl = bildeSti;
    const extra = { Innleggstype: i.type };
    if (i.avsenderRolle)
        extra['Rapportert av'] = i.avsenderRolle;
    if (i.merker.length)
        extra['Merker'] = i.merker.join(', ');
    if (forsteVideo) {
        extra['Video'] = `${Math.round((forsteVideo.varighetMs ?? 0) / 1000)} s`;
        // ⚠ Plakatbildet er ETT bilde av en video. Det skal stå, så en analytiker
        // ikke tror hun har sett hele opptaket fordi hun har sett stillbildet.
        extra['⚠ Plakatbilde'] = 'Stillbilde fra video — se hele opptaket i feeden';
    }
    o.extra = extra;
    return o;
}
/** Presedens-id-en som `PRECEDENCE_*`-tabellene i `precedence.ts` bruker. */
export function feedPresedens(i) {
    return i.presedens;
}
/**
 * Skal pushen bryte gjennom stillemodus?
 *
 * ⚠ BARE FLASH. Er svaret «immediate og oppover», lærer operatøren å slå av
 * varsler helt — og da kommer heller ikke FLASH fram. Et varsel som alltid
 * bryter gjennom er et varsel som blir skrudd av.
 */
export function bryterStillemodus(i) {
    return i.presedens === 'flash';
}
//# sourceMappingURL=feed.js.map